import React, { useRef, useCallback, useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { HiOutlineDocument } from 'react-icons/hi';
import { FiMapPin, FiHash } from 'react-icons/fi';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';

import api from '../../../../services/api';
import geographicApi from '../../../../services/geographicApi';
import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/toast';
import styleMaterial from './styles/styleMaterial';
import InputLogged from '../../../../components/InputLogged';
import Select from '../../../../components/Select';
import {
  Wrapper,
  PageTitle,
  InputWrapper,
  ButtonStyled,
  LittleInformativeSpan,
} from './styles';

// A aba Fiscal é a única que usa <Grid container spacing={3}> (gutter de 24px).
// O Wrapper compartilhado não tem padding horizontal, então o gutter negativo do
// Grid colava os inputs nas bordas. Este wrapper adiciona respiro lateral que
// acomoda o gutter — sem alterar o Wrapper usado pelas demais abas.
const FiscalWrapper = styled(Wrapper)`
  padding-left: 24px;
  padding-right: 24px;
`;

interface SelectOption {
  value: number | string;
  label: string;
}

interface FiscalFormData {
  inscricao_estadual: string;
  cnae: string;
  codigo_municipio_ibge: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  municipio: string;
  uf: string;
  cep: string;
}

// Ambiente fiscal: 1 = Produção, 2 = Homologação (AC1).
const ambienteOptions: SelectOption[] = [
  { value: '2', label: 'Homologação' },
  { value: '1', label: 'Produção' },
];

// Emissão da NFC-e dos pedidos do delivery. Padrão: manual.
const emissaoDeliveryOptions: SelectOption[] = [
  { value: 'manual', label: 'Manual (botão no monitor de pedidos)' },
  { value: 'automatica', label: 'Automática ao aceitar o pedido' },
];

/** 12345678000199 → 12.345.678/0001-99 (só exibição). */
const formatCnpj = (value: string): string => {
  const digits = (value || '').replace(/\D/g, '');
  if (digits.length !== 14) return value || '—';
  return digits.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5',
  );
};

const Fiscal: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const history = useHistory();

  const formRef = useRef<FormHandles>(null);
  const classes = styleMaterial();

  const [regimeOptions, setRegimeOptions] = useState<SelectOption[]>([]);
  const [regime, setRegime] = useState<SelectOption | null>(null);
  const [ambiente, setAmbiente] = useState<SelectOption | null>(null);
  // null até carregar: salvar sem ter lido a empresa não pode desligar a
  // emissão automática.
  const [emissaoDelivery, setEmissaoDelivery] = useState<SelectOption | null>(
    null,
  );
  const [emissaoHabilitada, setEmissaoHabilitada] = useState(false);
  const [cnpj, setCnpj] = useState('');
  const [buscandoIbge, setBuscandoIbge] = useState(false);
  const [initialData, setInitialData] = useState<FiscalFormData>({
    inscricao_estadual: '',
    cnae: '',
    codigo_municipio_ibge: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    municipio: '',
    uf: '',
    cep: '',
  });

  useEffect(() => {
    async function loadData(): Promise<void> {
      try {
        const [{ data: empresa }, { data: regimes }] = await Promise.all([
          api.get(`/empresa/${user.empresa_id}`),
          api.get('/regimes-tributarios'),
        ]);

        const options: SelectOption[] = (regimes || []).map((r: any) => ({
          value: r.id,
          label: r.descricao,
        }));
        setRegimeOptions(options);

        const endereco = empresa.endereco || {};

        // Código IBGE ausente e endereço preenchido: resolve pelo município/UF
        // em vez de exigir que o usuário procure os 7 dígitos.
        let codigoIbge = empresa.codigo_municipio_ibge || '';
        if (!codigoIbge && endereco.cidade && endereco.estado) {
          try {
            codigoIbge =
              (await geographicApi.getCodigoIbgeMunicipio(
                endereco.estado,
                endereco.cidade,
              )) || '';
          } catch {
            codigoIbge = '';
          }
        }

        setInitialData({
          inscricao_estadual: empresa.inscricao_estadual || '',
          cnae: empresa.cnae || '',
          codigo_municipio_ibge: codigoIbge,
          logradouro: endereco.logradouro || '',
          numero: endereco.numero || '',
          complemento: endereco.complemento || '',
          bairro: endereco.bairro || '',
          // jsonb existente usa cidade/estado; exibimos como Município/UF (§4.4).
          municipio: endereco.cidade || '',
          uf: endereco.estado || '',
          cep: endereco.cep || '',
        });

        setRegime(
          options.find((o) => o.value === empresa.regime_tributario_id) || null,
        );
        setAmbiente(
          ambienteOptions.find(
            (o) => String(o.value) === String(empresa.ambiente_fiscal),
          ) || null,
        );
        setEmissaoHabilitada(!!empresa.emissao_habilitada);
        setEmissaoDelivery(
          emissaoDeliveryOptions[empresa.nfce_delivery_automatica ? 1 : 0],
        );
        setCnpj(empresa.cnpj_cpf || '');
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro ao carregar dados fiscais',
        });
      }
    }

    loadData();
  }, [user.empresa_id, addToast]);

  // Busca o código IBGE a partir do município/UF digitados no formulário.
  const handleBuscarIbge = useCallback(async () => {
    const municipio = String(
      formRef.current?.getFieldValue('municipio') || '',
    ).trim();
    const uf = String(formRef.current?.getFieldValue('uf') || '').trim();

    if (!municipio || !uf) {
      addToast({
        type: 'error',
        title: 'Informe município e UF',
        description: 'O código IBGE é localizado a partir do município e da UF.',
      });
      return;
    }

    setBuscandoIbge(true);
    try {
      const codigo = await geographicApi.getCodigoIbgeMunicipio(uf, municipio);

      if (!codigo) {
        addToast({
          type: 'error',
          title: 'Município não encontrado',
          description: `Não encontramos "${municipio}" em ${uf.toUpperCase()}. Confira a grafia ou informe o código manualmente.`,
        });
        return;
      }

      formRef.current?.setFieldValue('codigo_municipio_ibge', codigo);
      formRef.current?.setFieldError('codigo_municipio_ibge', '');
      addToast({
        type: 'success',
        title: 'Código IBGE preenchido',
        description: `${municipio}/${uf.toUpperCase()} → ${codigo}. Não esqueça de salvar.`,
      });
    } catch {
      addToast({
        type: 'error',
        title: 'Erro ao consultar o IBGE',
        description: 'Serviço indisponível. Informe o código manualmente.',
      });
    } finally {
      setBuscandoIbge(false);
    }
  }, [addToast]);

  const handleSubmit = useCallback(
    async (data: FiscalFormData) => {
      formRef.current?.setErrors({});

      const ibge = (data.codigo_municipio_ibge || '').trim();
      if (ibge && !/^\d{7}$/.test(ibge)) {
        formRef.current?.setFieldError(
          'codigo_municipio_ibge',
          'O código IBGE deve ter 7 dígitos numéricos.',
        );
        addToast({
          type: 'error',
          title: 'Código IBGE inválido',
          description: 'Informe os 7 dígitos do município (IBGE).',
        });
        return;
      }

      try {
        await api.put(`/empresa/${user.empresa_id}`, {
          // Payload PARCIAL: o backend (UpdateEmpresaService) só atualiza os
          // campos enviados. keepTimezone evita o reset do fuso, que só é
          // recalculado quando lat/lon são enviados (tela Loja).
          keepTimezone: true,
          inscricao_estadual: data.inscricao_estadual,
          cnae: data.cnae,
          codigo_municipio_ibge: ibge || null,
          regime_tributario_id: regime?.value ?? null,
          ambiente_fiscal: ambiente?.value ?? null,
          ...(emissaoDelivery
            ? {
                nfce_delivery_automatica:
                  emissaoDelivery.value === 'automatica',
              }
            : {}),
          endereco: {
            logradouro: data.logradouro,
            numero: data.numero,
            complemento: data.complemento,
            bairro: data.bairro,
            // grava nos campos existentes do jsonb (cidade/estado).
            cidade: data.municipio,
            estado: data.uf,
            cep: data.cep,
          },
        });

        addToast({
          type: 'success',
          title: 'Dados fiscais atualizados!',
          description: 'As informações fiscais da loja foram salvas.',
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro ao salvar dados fiscais',
          description:
            (err as any).response?.data?.message ||
            'Verifique os dados e tente novamente.',
        });
      }
    },
    [user.empresa_id, regime, ambiente, emissaoDelivery, addToast],
  );

  return (
    <FiscalWrapper>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PageTitle>Dados fiscais (NFC-e)</PageTitle>
          <LittleInformativeSpan>
            Cadastro fiscal necessário para a emissão de notas. A ativação da
            emissão (certificado, CSC e ambiente) é feita no assistente de
            habilitação fiscal.
          </LittleInformativeSpan>

          <Grid container style={{ marginTop: 12 }}>
            <Grid item lg={4} md={6} xs={12}>
              <ButtonStyled
                type="button"
                onClick={() => history.push('/configuracoes/ativar-emissao')}
              >
                {emissaoHabilitada
                  ? 'Gerenciar emissão (assistente)'
                  : 'Habilitar emissão de NFC-e'}
              </ButtonStyled>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Form
            ref={formRef}
            onSubmit={handleSubmit}
            className={classes.root}
            initialData={initialData}
          >
            <Grid container spacing={3}>
              <Grid item lg={6} md={6} xs={12}>
                <InputWrapper>
                  <div className="numero_empresa">
                    <span>CNPJ:</span>
                    {/* Só leitura: o CNPJ é o do cadastro da empresa e é o que
                        vai no emitente da NFC-e — alterá-lo aqui invalidaria o
                        tenant já provisionado na atac-notas. */}
                    <p>{formatCnpj(cnpj)}</p>
                  </div>
                </InputWrapper>
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <InputWrapper>
                  <div className="numero_empresa">
                    <span>Emissão habilitada:</span>
                    <p>{emissaoHabilitada ? 'Sim' : 'Não'}</p>
                  </div>
                </InputWrapper>
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <InputWrapper>
                  <span>Inscrição Estadual:</span>
                  <InputLogged
                    name="inscricao_estadual"
                    placeholder="Inscrição Estadual"
                    icon={HiOutlineDocument}
                  />
                </InputWrapper>
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <InputWrapper>
                  <span>CNAE:</span>
                  <InputLogged
                    name="cnae"
                    placeholder="CNAE principal"
                    icon={HiOutlineDocument}
                  />
                </InputWrapper>
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <InputWrapper>
                  <span>Regime Tributário:</span>
                  <Select
                    name="regime_tributario_id"
                    placeholder="Selecione o regime"
                    value={regime}
                    onChange={(value) => setRegime(value as SelectOption)}
                    options={regimeOptions}
                  />
                </InputWrapper>
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <InputWrapper>
                  <span>Ambiente Fiscal:</span>
                  <Select
                    name="ambiente_fiscal"
                    placeholder="Selecione o ambiente"
                    value={ambiente}
                    onChange={(value) => setAmbiente(value as SelectOption)}
                    options={ambienteOptions}
                  />
                </InputWrapper>
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <InputWrapper>
                  <span>Emissão da NFC-e dos pedidos do delivery:</span>
                  <Select
                    name="nfce_delivery_emissao"
                    value={emissaoDelivery}
                    onChange={(value) =>
                      setEmissaoDelivery(value as SelectOption)
                    }
                    options={emissaoDeliveryOptions}
                  />
                  <LittleInformativeSpan>
                    Na automática, a nota é emitida em até 1 minuto depois de o
                    pedido ser aceito, no terminal da retaguarda e com o CPF
                    informado no pedido. Vale só para pedidos criados depois de
                    ativar a opção.
                  </LittleInformativeSpan>
                </InputWrapper>
              </Grid>

              <Grid item xs={12}>
                <PageTitle>Endereço fiscal</PageTitle>
              </Grid>

              <Grid item lg={8} md={8} xs={12}>
                <InputWrapper>
                  <span>Logradouro:</span>
                  <InputLogged
                    name="logradouro"
                    placeholder="Rua / Avenida"
                    icon={FiMapPin}
                  />
                </InputWrapper>
              </Grid>

              <Grid item lg={4} md={4} xs={12}>
                <InputWrapper>
                  <span>Número:</span>
                  <InputLogged name="numero" placeholder="Número" icon={FiHash} />
                </InputWrapper>
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <InputWrapper>
                  <span>Complemento:</span>
                  <InputLogged
                    name="complemento"
                    placeholder="Complemento"
                    icon={FiMapPin}
                  />
                </InputWrapper>
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <InputWrapper>
                  <span>Bairro:</span>
                  <InputLogged name="bairro" placeholder="Bairro" icon={FiMapPin} />
                </InputWrapper>
              </Grid>

              <Grid item lg={5} md={5} xs={12}>
                <InputWrapper>
                  <span>Município:</span>
                  <InputLogged
                    name="municipio"
                    placeholder="Município"
                    icon={FiMapPin}
                  />
                </InputWrapper>
              </Grid>

              <Grid item lg={2} md={2} xs={12}>
                <InputWrapper>
                  <span>UF:</span>
                  <InputLogged name="uf" placeholder="UF" icon={FiMapPin} />
                </InputWrapper>
              </Grid>

              <Grid item lg={5} md={5} xs={12}>
                <InputWrapper>
                  <span>CEP:</span>
                  <InputLogged name="cep" placeholder="CEP" icon={FiMapPin} />
                </InputWrapper>
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <InputWrapper>
                  <span>Código do Município (IBGE):</span>
                  <InputLogged
                    name="codigo_municipio_ibge"
                    placeholder="7 dígitos (IBGE)"
                    icon={FiHash}
                  />
                  <LittleInformativeSpan>
                    Código IBGE de 7 dígitos do município da loja.
                  </LittleInformativeSpan>
                  <ButtonStyled
                    type="button"
                    onClick={handleBuscarIbge}
                    disabled={buscandoIbge}
                  >
                    {buscandoIbge
                      ? 'Buscando…'
                      : 'Buscar pelo município/UF'}
                  </ButtonStyled>
                </InputWrapper>
              </Grid>

              <Grid item xs={12}>
                <Grid container justify="center">
                  <Grid item lg={4} md={6} xs={12}>
                    <ButtonStyled type="submit">Salvar</ButtonStyled>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        </Grid>
      </Grid>
    </FiscalWrapper>
  );
};

export default Fiscal;
